/**
 * The password rule, in one place, in the product's own words.
 *
 * The rule is NOT invented here. The installation's policy is
 * `settings.security.passwordPolicyMinLength` in `backend/src/composition.ts` — twelve characters —
 * which `security_settings.read` reports as `{ status: 'configured', minLength: 12 }` (PO 7.2
 * §11.1). The recovery screen cannot ask for that value, because the person setting a password is
 * by definition not signed in and `security_settings.read` is an authorized internal command. So
 * the number is mirrored, and `tests/fixtures/password-policy.json` is the canonical copy both
 * packages test against, so the figure a screen prints and the figure the server is configured
 * with cannot drift apart.
 *
 * The upper bound is the provider's, not a policy: Supabase Auth hashes with bcrypt, which reads
 * only the first 72 BYTES. A longer password is not rejected, it is silently truncated — so a
 * person who typed a long Turkish passphrase would be signing in with a prefix of it without ever
 * being told. Refusing it here is the only way they find out.
 *
 * Nothing else is asserted. No composition rule (an upper-case letter, a digit, a symbol) is
 * claimed, because this project's contract states none, and a Supabase project can carry
 * requirements of its own that this build cannot read; when it does, the provider's refusal is
 * mapped to a safe sentence rather than pre-empted with a guess.
 */

/** `backend/src/composition.ts` → `security.passwordPolicyMinLength`. Counted in characters. */
export const PASSWORD_MIN_LENGTH = 12;

/** bcrypt's own limit, counted in UTF-8 bytes — `ş` costs two, an emoji four. */
export const PASSWORD_MAX_BYTES = 72;

/** Shown on the screen, before anything is typed, so the rule is never a surprise. */
export const PASSWORD_RULE_TEXT =
  `Parolanız en az ${PASSWORD_MIN_LENGTH} karakter olmalı. ` +
  'Uzun bir parola, kısa ve karmaşık bir paroladan daha güçlüdür; boşluk kullanabilirsiniz ve ' +
  'yazdığınız parola kırpılmaz.';

const encoder = new TextEncoder();

/** UTF-8 length, which is what bcrypt counts. */
export function passwordByteLength(password: string): number {
  return encoder.encode(password).length;
}

/**
 * The one check the screen runs before it calls the provider.
 *
 * Returns Turkish text when the pair may not be submitted, `null` when it may. Deliberately
 * ordered: a password that is too short is told so before it is compared, because «iki parola aynı
 * değil» is unhelpful when neither of them was long enough to be accepted anyway.
 *
 * The password is never trimmed. A leading or trailing space is part of what the person chose, and
 * silently removing it here would mean they could not sign in with what they typed.
 */
export function passwordProblem(password: string, confirmation: string): string | null {
  /* Code points, not UTF-16 units: twelve Turkish characters are twelve characters. */
  if ([...password].length < PASSWORD_MIN_LENGTH) {
    return `Parolanız en az ${PASSWORD_MIN_LENGTH} karakter olmalı.`;
  }
  if (passwordByteLength(password) > PASSWORD_MAX_BYTES) {
    return 'Parolanız çok uzun. Türkçe karakterler fazladan yer kaplar; daha kısa bir parola seçin.';
  }
  if (password !== confirmation) {
    return 'İki parola aynı değil. İkisini de kontrol edip yeniden yazın.';
  }
  return null;
}
